import { Injectable } from '@angular/core';

import { ManagementUser, ManagementUserAdapter } from '../../../core/models/management-user.model';
import { Student } from '../../../core/models/student.model';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { mergeMap, Observable, of } from 'rxjs';
import { ReportsService } from 'src/app/core/services/reports.service';

/**
 * Service methods for the school-admin-teacher-filter component
 */
@Injectable({
  providedIn: 'root'
})
export class SchoolAdminTeacherFilterService {
  constructor(
    private sessionStorageService: SessionStorageService,
    private reportsService: ReportsService,
    private managementAdapter: ManagementUserAdapter,
  ) {}

  /**
   * This method should be called in the resolver for any route that uses the
   * school-admin-teacher-filter component.
   *
   * Fetches all active teachers that the school admin would have access to and
   * sets the teacher list and currently selected teacher in session storage.
   */
  initSchoolAdminTeacherFilter(opts: {allowAllOption: boolean} = {allowAllOption: false}): Observable<{loaded: boolean}> {
    // Do not load teachers if we already have them
    const currentTeacherFilterData = this.sessionStorageService.getSchoolAdminTeacherFilter();
    if (currentTeacherFilterData != null && currentTeacherFilterData.teachers.length > 0) {
      const currentSelectedTeacherId = currentTeacherFilterData.selectedTeacherId;
      // If "all" is selected, but we do not allow the "all" option, fallback to the first teacher
      if (!opts.allowAllOption && currentSelectedTeacherId == 0) {
        this.sessionStorageService.setSchoolAdminTeacherFilter({
          ...currentTeacherFilterData,
          selectedTeacherId: currentTeacherFilterData.teachers.length > 0 ? currentTeacherFilterData.teachers[0].userID : null,
        });
      }
      return of({
        loaded: true,
      });
    }

    const currentUser = this.sessionStorageService.userData();
    if (!currentUser?.isSchoolAdmin()) {
      this.sessionStorageService.setSchoolAdminTeacherFilter({
        teachers: [],
        selectedTeacherId: null,
      });

      return of({
        loaded: true,
      });
    }

    return this.reportsService.getAllActiveTeachers(currentUser, currentUser.schoolID).pipe(
      mergeMap((teachers) => {
        const sortedTeachers = this.sortTeachers(teachers).map(this.managementAdapter.adapt);
        this.sessionStorageService.setSchoolAdminTeacherFilter({
          teachers: sortedTeachers,
          selectedTeacherId: this.sessionStorageService.getSchoolAdminTeacherFilter()?.selectedTeacherId
            ?? sortedTeachers[0]?.userID
            ?? null,
        });

        return of({
          loaded: true,
        });
      }),
    );
  }

  sortTeachers(teachers: ManagementUser[]): ManagementUser[] {
    return [...teachers]
      .sort((a, b) => {
        const firstNameCompare = a.firstName.localeCompare(b.firstName);
        if (firstNameCompare !== 0) {
          return firstNameCompare;
        }

        return a.lastName.localeCompare(b.lastName);
      });
  }

  filterStudentsBySelectedTeacher(students: Student[]): Student[] {
    const selectedTeacherId = this.getSelectedTeacherId();
    if (selectedTeacherId == null || selectedTeacherId <= 0) {
      return students;
    }

    return students.filter((student) => student.teacherID === selectedTeacherId);
  }

  getSelectedTeacherName(): string | null {
    const teachers = this.sessionStorageService.getSchoolAdminTeacherFilter()?.teachers ?? [];
    const selectedTeacher = teachers.find(t => t.userID === this.getSelectedTeacherId());
    if (selectedTeacher == null) {
      return null;
    }

    return `${selectedTeacher.firstName} ${selectedTeacher.lastName}`;
  }

  getTeacherNamesById(): Record<number, string> {
    const teacherList = this.sessionStorageService.getSchoolAdminTeacherFilter()?.teachers ?? [];
    const teacherNamesById: Record<number, string> = {};
    for (const teacher of teacherList) {
      teacherNamesById[teacher.userID] = `${teacher.firstName} ${teacher.lastName}`;
    }
    return teacherNamesById;
  }

  getSelectedTeacherId(): number | null {
    return this.sessionStorageService.getSchoolAdminTeacherFilter()?.selectedTeacherId ?? null;
  }
}
